import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  LinearProgress,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Assignment, AccessTime } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { WorkOrderOperation } from '../../../store/slices/operatorSlice';

interface WorkOrderQueueProps {
  onSelectWorkOrder: (wo: WorkOrderOperation) => void;
}

const WorkOrderQueue: React.FC<WorkOrderQueueProps> = ({ onSelectWorkOrder }) => {
  const { assignedWorkOrders, activeOperation, isLoading } = useSelector(
    (state: RootState) => state.operator
  );

  const getPriorityColor = (
    priority: WorkOrderOperation['priority']
  ): 'error' | 'warning' | 'info' | 'success' | 'default' => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (
    status: WorkOrderOperation['status']
  ): 'warning' | 'info' | 'success' | 'error' | 'default' => {
    switch (status) {
      case 'started':
        return 'warning';
      case 'paused':
        return 'info';
      case 'completed':
        return 'success';
      case 'on_hold':
        return 'error';
      case 'queued':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDueDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHrs = Math.round(diffMs / (1000 * 60 * 60));

    if (diffHrs < 0) return { label: `${Math.abs(diffHrs)}h overdue`, color: 'error.main' };
    if (diffHrs < 8) return { label: `Due in ${diffHrs}h`, color: 'warning.main' };
    return { label: date.toLocaleDateString(), color: 'textSecondary' };
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (assignedWorkOrders.length === 0) {
    return (
      <Alert severity="info" icon={<Assignment />}>
        No work orders assigned to this workstation. Check with your supervisor.
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent sx={{ pb: '8px !important' }}>
        <Typography variant="h6" gutterBottom>
          Work Order Queue ({assignedWorkOrders.length})
        </Typography>
        <List disablePadding>
          {assignedWorkOrders.map((wo, index) => {
            const isActive = activeOperation?.id === wo.id;
            const progressPercent =
              wo.plannedQty > 0 ? (wo.producedQty / wo.plannedQty) * 100 : 0;
            const dueInfo = formatDueDate(wo.dueDate);

            return (
              <React.Fragment key={wo.id}>
                {index > 0 && <Divider />}
                <ListItem
                  button
                  onClick={() => onSelectWorkOrder(wo)}
                  selected={isActive}
                  sx={{
                    borderRadius: 1,
                    my: 0.5,
                    px: 1.5,
                    py: 1.5,
                    border: isActive ? '2px solid' : '1px solid transparent',
                    borderColor: isActive ? 'primary.main' : 'transparent',
                    '&.Mui-selected': {
                      bgcolor: 'primary.50',
                    },
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="body1" fontWeight={600}>
                          {wo.orderNumber}
                        </Typography>
                        <Chip
                          label={wo.priority.toUpperCase()}
                          color={getPriorityColor(wo.priority)}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={wo.status.toUpperCase()}
                          color={getStatusColor(wo.status)}
                          size="small"
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary" mb={0.5}>
                          {wo.productName} — {wo.operationDescription}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={2} mb={0.75}>
                          <Typography variant="caption" color={dueInfo.color} display="flex" alignItems="center" gap={0.5}>
                            <AccessTime sx={{ fontSize: 12 }} />
                            {dueInfo.label}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Est: {wo.estimatedHours}h
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LinearProgress
                            variant="determinate"
                            value={progressPercent}
                            sx={{ flex: 1, height: 6, borderRadius: 3 }}
                            color={wo.status === 'completed' ? 'success' : 'primary'}
                          />
                          <Typography variant="caption" color="textSecondary" sx={{ minWidth: 70 }}>
                            {wo.producedQty}/{wo.plannedQty} pcs
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                  {isActive && (
                    <ListItemSecondaryAction>
                      <Typography variant="caption" color="primary" fontWeight={600}>
                        ACTIVE
                      </Typography>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      </CardContent>
    </Card>
  );
};

export default WorkOrderQueue;
